import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  questionTimeLimitMs,
  type NameMode,
  type ServerMessage,
} from "@binogi-livequiz/shared";
import Wordmark from "../components/Wordmark";
import { CharacterImage } from "../components/CharacterImage";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { serverErrorKey } from "../lib/i18n";
import { useWebSocket } from "../lib/ws-context";
import { loadStudentSession } from "../lib/session-storage";
import { useStudentRejoin } from "../hooks/useStudentRejoin";

interface RouteState {
  sessionCode: string;
  displayName: string;
  playerCount?: number;
  nameMode: NameMode;
  expectedStudents?: number;
  availableLanguages?: string[];
}

export default function PlayWaiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWebSocket();
  const { t } = useTranslation();
  // Join hands the session over in route state. Without it (an iPhone
  // reloads a page that slept, and the state does not survive) the screen
  // falls back to the session saved on this device and rejoins that, instead
  // of stranding the student on "Back to join". The room count is left unset
  // here and comes from the rejoin's SESSION_JOINED.
  const state = useMemo<RouteState | null>(() => {
    const routeState = location.state as RouteState | null;
    if (routeState) return routeState;
    const saved = loadStudentSession();
    if (!saved) return null;
    return {
      sessionCode: saved.sessionCode,
      displayName: saved.displayName,
      nameMode: saved.nameMode,
      availableLanguages: saved.availableLanguages,
    };
  }, [location.state]);
  const [playerCount, setPlayerCount] = useState<number>(
    state?.playerCount ?? 1,
  );
  const [expectedStudents, setExpectedStudents] = useState<number>(
    state?.expectedStudents ?? 0,
  );
  // A refused rejoin (the session finished while the phone slept, say), as an
  // i18n key so it re-renders in the student's language.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // `myName` is the name the server currently knows us by — it can differ
  // from the one in route state after a rejoin that hit a name collision.
  const { displayName: myName, superseded } = useStudentRejoin(state);

  useEffect(() => {
    const m = ws.lastMessage;
    if (!m) return;
    handle(m);
    function handle(msg: ServerMessage) {
      if (msg.type === "QUESTION_START") {
        ws.consume();
        navigate("/play/question", {
          state: {
            sessionCode: state?.sessionCode,
            displayName: myName,
            questionIndex: msg.questionIndex,
            totalQuestions: msg.totalQuestions,
            timeLimitMs: questionTimeLimitMs(msg),
            question: msg.question,
            startedAt: Date.now(),
            availableLanguages: state?.availableLanguages,
          },
        });
      } else if (msg.type === "SESSION_FINISHED") {
        ws.consume();
        navigate("/play/finished", {
          state: {
            displayName: myName,
            finalLeaderboard: msg.finalLeaderboard,
            availableLanguages: state?.availableLanguages,
          },
        });
      } else if (msg.type === "PLAYER_JOINED" || msg.type === "PLAYER_LEFT") {
        setPlayerCount(msg.playerCount);
        ws.consume();
      } else if (msg.type === "SESSION_JOINED") {
        // useStudentRejoin's effect, declared before this one, has already
        // saved the new name and token from this ack, so consuming is safe.
        setPlayerCount(msg.playerCount);
        setExpectedStudents(msg.expectedStudents);
        // A later rejoin that succeeds clears an earlier refusal.
        setErrorKey(null);
        ws.consume();
      } else if (msg.type === "ERROR") {
        ws.consume();
        setErrorKey(
          serverErrorKey(
            msg,
            state?.nameMode === "character"
              ? { overrides: { NAME_TAKEN: "errors.character_busy" } }
              : {},
          ),
        );
      }
    }
  }, [myName, navigate, state, ws]);

  if (!state || errorKey) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-white/70">{t(errorKey ?? "play.not_joined")}</p>
          <button
            type="button"
            onClick={() => navigate("/join")}
            className="mt-4 px-4 py-2 bg-brand-purple text-white rounded-lg"
          >
            {t("play.back_to_join")}
          </button>
        </div>
      </main>
    );
  }

  const progressPct =
    expectedStudents > 0
      ? Math.min(100, Math.round((playerCount / expectedStudents) * 100))
      : 0;
  const filled = expectedStudents > 0 && playerCount >= expectedStudents;

  const langPickerCodes =
    state.availableLanguages ??
    loadStudentSession()?.availableLanguages;

  return (
    <main className="min-h-full flex flex-col items-center justify-center px-6 py-12 text-center relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher available={langPickerCodes} />
      </div>
      <div className="mb-10">
        <Wordmark size="lg" />
      </div>

      {superseded && (
        <p
          role="status"
          className="mb-4 rounded-xl border border-brand-amber/50 bg-brand-amber/20 px-4 py-3 text-center text-sm font-semibold"
        >
          {t("play.superseded")}
        </p>
      )}

      {state.nameMode === "character" ? (
        <>
          <CharacterImage
            name={myName}
            size={220}
            className="mb-4 drop-shadow-2xl animate-pop"
          />
          <p className="text-white/70 mb-2">{t("play.waiting.you_are")}</p>
          <h1 className="text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-brand-teal to-brand-amber bg-clip-text text-transparent">
            {myName}
          </h1>
          <p className="mt-5 text-white/70">{t("play.waiting.get_ready")}</p>
        </>
      ) : (
        <>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-2">
            {t("play.waiting.get_ready_named", { name: myName })}
          </h1>
        </>
      )}

      <div className="mt-10 w-full max-w-sm">
        {expectedStudents > 0 ? (
          <>
            <div className="flex items-baseline justify-between mb-2 text-sm text-white/70">
              <span>{t("play.waiting.in_room_label")}</span>
              <span
                key={playerCount}
                className="tabular-nums font-semibold text-white animate-pop inline-block"
              >
                {playerCount} / {expectedStudents}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-brand-ink2 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  filled ? "bg-brand-green" : "bg-brand-teal"
                }`}
                style={{ width: `${Math.max(progressPct, 3)}%` }}
              />
            </div>
          </>
        ) : (
          <div
            key={playerCount}
            className="flex items-center justify-center gap-3 text-white/60 animate-pop"
          >
            <span className="w-2 h-2 rounded-full bg-brand-green animate-pulseSoft" />
            <span className="text-sm">
              {t("play.waiting.in_room", { count: playerCount })}
            </span>
          </div>
        )}
      </div>
    </main>
  );
}
